import { SCUICustomDataGrid } from '@/atomic/molecules/SCUICustomDataGrid/SCUICustomDataGrid';
import { SCUIButton } from '../../../atomic/atoms';
import { Box, FormControlLabel, Switch, IconButton, Menu, MenuItem, ListItemIcon, ListItemText } from '@mui/material';
import { useTranslation } from 'react-i18next';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import EditIcon from '@mui/icons-material/Edit';
import HistoryIcon from '@mui/icons-material/History';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import { downloadExcel } from '@/utils/excelFileHelper';
import PopupState, { bindTrigger, bindMenu } from 'material-ui-popup-state';
import { GROW } from '../growManagement.classes';

export const GrowListPage = ({ setModalOpen, onLogOpen, growList }: any) => {
    const { t } = useTranslation();

    const formatWarehouseLabel = (warehouseCode?: string, warehouseName?: string) =>
        warehouseName ? `${warehouseCode} - ${warehouseName}` : warehouseCode || '';

    const columns = [
        {
            field: 'warehouseCode',
            headerName: t('growManagement.warehouseCode'),
            flex: 1.5,
            renderCell: (params: any) => formatWarehouseLabel(params.row?.warehouseCode, params.row?.warehouseName),
        },
        {
            field: 'status',
            headerName: t('growManagement.status'),
            flex: 1,
            renderCell: (params: any) => (
                <FormControlLabel
                    className={GROW.datagridLabelStatus}
                    control={<Switch className={GROW.inputSwitchStatusReadonly} checked={params.value} color="primary" disabled />}
                    label={params.value ? t('general.active') : t('general.passive')}
                />
            ),
        },
        {
            field: 'warehouseType',
            headerName: t('growManagement.warehouseType'),
            flex: 1,
            renderCell: () => <p>GROW</p>,
        },
        { field: 'totalQuantity', headerName: t('growManagement.totalQuantity'), flex: 1 },
        {
            field: 'growDailyLimit',
            headerName: t('growManagement.dailyLimit'),
            flex: 1,
            renderCell: (params: any) => params.value ?? '-',
        },
        {
            field: 'growStockSourceValue',
            headerName: t('growManagement.stockSource'),
            flex: 1,
            renderCell: (params: any) => params.value || '-',
        },
        {
            field: 'growBufferSize',
            headerName: t('growManagement.growBufferSize'),
            flex: 1,
            renderCell: (params: any) => (
                <Box
                    className={GROW.datagridCellBufferBadge}
                    sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        width: '24px',
                        height: '24px',
                        borderRadius: '50%',
                        backgroundColor: '#e0e0e0',
                        padding: '2px',
                    }}
                >
                    <p>{params.value}</p>
                </Box>
            ),
        },
        {
            field: 'actions',
            headerName: '',
            flex: 0.5,
            sortable: false,
            renderCell: (params: any) => (
                <PopupState variant="popover" popupId="grow-management-actions-menu">
                    {(popupState: any) => (
                        <>
                            <IconButton className={GROW.inputButtonRowActions} {...bindTrigger(popupState)}>
                                <MoreVertIcon className="svg_icons" />
                            </IconButton>
                            <Menu {...bindMenu(popupState)}>
                                <MenuItem
                                    className={GROW.inputMenuItemEdit}
                                    onClick={() => {
                                        setModalOpen(params.row);
                                        popupState.close();
                                    }}
                                >
                                    <ListItemIcon>
                                        <EditIcon fontSize="small" />
                                    </ListItemIcon>
                                    <ListItemText>{t('growManagement.edit')}</ListItemText>
                                </MenuItem>
                                <MenuItem
                                    className={GROW.inputMenuItemViewLogs}
                                    onClick={() => {
                                        onLogOpen(params.row);
                                        popupState.close();
                                    }}
                                >
                                    <ListItemIcon>
                                        <HistoryIcon fontSize="small" />
                                    </ListItemIcon>
                                    <ListItemText>{t('growManagement.viewLogs')}</ListItemText>
                                </MenuItem>
                            </Menu>
                        </>
                    )}
                </PopupState>
            ),
        },
    ];

    const handleExportExcel = () => {
        const data = (growList || []).map((item: any) => ({
            warehouseCode: formatWarehouseLabel(item.warehouseCode, item.warehouseName),
            status: item.status ? t('general.active') : t('general.passive'),
            warehouseType: 'GROW',
            totalQuantity: item.totalQuantity,
            growDailyLimit: item.growDailyLimit ?? '-',
            growStockSourceValue: item.growStockSourceValue || '-',
            growBufferSize: item.growBufferSize,
        }));

        downloadExcel({
            excelDownloadData: data,
            excelDownloadFolderName: 'Grow_' + new Date().toJSON().slice(0, 10).replace(/-/g, '_'),
        });
    };

    return (
        <>
            <Box className={GROW.listSection} sx={{ width: '100%' }}>
                <Box className={GROW.listToolbar} sx={{ margin: '12px 0', display: 'flex', alignItems: 'center' }}>
                    <p className={GROW.listTitle} style={{ fontSize: '16px' }}>
                        {t('growManagement.listTitle')}
                    </p>{' '}
                    <SCUIButton
                        className={GROW.inputButtonExportExcel}
                        text={t('general.exportToExcel')}
                        type="secondary"
                        sizeType="fixed"
                        style={{ height: '35px', marginLeft: 'auto' }}
                        size={'m'}
                        buttonType="button"
                        onClick={handleExportExcel}
                        showStartIcon
                        startIcon={<FileDownloadIcon sx={{ color: '#0d6efd' }} />}
                    />
                </Box>
                <Box className={GROW.gridWrap}>
                    <SCUICustomDataGrid
                        rows={growList || []}
                        hideFooter
                        allrecord
                        columns={columns}
                        getRowId={(row: any) => row.warehouseId}
                    />
                </Box>
            </Box>
        </>
    );
};
